<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <QTable
                    :columns="columns"
                    :data="tableData"
                    hide-bottom
                    class="sticky-table"
                    :rows-per-page-options="[0]"
                >
                    <template v-slot:header="props">
                        <q-tr :props="props">
                            <q-th
                                v-for="col in props.cols"
                                :key="col.name"
                                :props="props"
                                class="sticky-header"
                            >
                                <strong>{{ col.label }}</strong>
                            </q-th>
                        </q-tr>
                        <q-linear-progress
                            v-if="isLoading"
                            class="sticky-loader"
                            indeterminate
                            color="dark"
                        />
                    </template>
                    <template
                        v-slot:bottom-row
                        v-if="tableData.length == 0 && !isLoading && !isError"
                    >
                        <q-tr>
                            <q-td colspan="12">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:body-cell="{ row, value, col }">
                        <q-td :class="col.__tdClass" :style="col.style">
                            <strong v-if="row.payment_date == 'Total'">
                                {{ value }}
                            </strong>
                            <client-name
                                v-else-if="col.field == 'client_info'"
                                :use-router="USE_CLIENT_MODULE_VUE"
                                :client="generateClientObject(row)"
                            />
                            <span v-else>{{ value }}</span>
                        </q-td>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import "@/css/quasar-table.css";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import ClientName from "@/components/commons/client-name";
export default {
    mixins: [Report], // INTERVAL_TIME, START_PAGE AND RECORD_PER_PAGE in here
    components: {
        QTable,
        ClientName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("day"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    data() {
        return {
            columns: [
                {
                    name: "payment_date",
                    field: "payment_date",
                    label: "Payment Date",
                    align: "left",
                    headerStyle: "font-weight:bold;",
                    style: "width: 15%;"
                },
                {
                    name: "client_info",
                    field: "client_info",
                    label: "Client Name",
                    align: "left",
                    headerStyle: "font-weight:bold;",
                    style: "width: 25%;"
                },
                {
                    name: "payment_amount",
                    field: "payment_amount",
                    label: "Payment Amount",
                    align: "left",
                    headerStyle: "font-weight:bold;",
                    style: "width: 15%;"
                },
                {
                    name: "payment_type",
                    field: "payment_type",
                    label: "Payment Type",
                    align: "left",
                    headerStyle: "font-weight:bold;",
                    style: "width: 15%;"
                },
                {
                    name: "posted_by",
                    field: "posted_by",
                    label: "Posted By",
                    align: "left",
                    headerStyle: "font-weight:bold;",
                    style: "width: 20%;"
                },
                {
                    name: "paid_online",
                    field: "paid_online",
                    label: "Paid Online",
                    align: "left",
                    headerStyle: "font-weight:bold;",
                    style: "width: 10%;"
                }
            ],
            tableData: [],
            isLoading: true,
            title: "Collections Detail By Type",
            dateRange: "",
            USE_CLIENT_MODULE_VUE: process.env.USE_CLIENT_MODULE_VUE === "true",
            isDestroyed: false,
            nextPage: 1
        };
    },

    async mounted() {
        let self = this;
        self.toggleOptions(false);
        self.data.exportOption = false; // this report only has csv
        self.data.isShowDateRange = true;

        self.data.datePickerOptionDefault = 1;

        await self._getSetting();
        self.data.start = this.getStartDate("day");
        self.data.end = this.getEndDate("day");
        self.data.from = self.converToUnix(self.data.start);
        self.data.to = self.converToUnix(self.data.end);

        self.isDestroyed = false;
        self.tableData = [];
        self.init();

        self.$root.$on("report-filter-date-range:refresh", self.prepareData);

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.exportOptionCsvOnly = false;
        this.data.datePickerOptionDefault = null;
        this.data.exportOption = true;
        this.data.filterDateOption = true;
        this.data.refreshOption = true;
        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
        this.isDestroyed = true;
    },

    methods: {
        prepareData(params) {
            this.isDestroyed = false;
            this.isLoading = true;
            this.tableData = [];
            this.nextPage = this.START_PAGE;
            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
            }
            this.toggleOptions(false);
            this.init(params);
        },
        async init(params) {
            let self = this;
            self.isError = false;
            self.dateRange = self.getDateRange(self.data.from, self.data.to);
            params = {
                page: this.nextPage,
                perPage: self.RECORD_PER_PAGE,
                from: self.data.from,
                to: self.data.to
            };

            if (!self.isDestroyed) {
                ReportService.getReport(
                    CREPORT.TYPE.COLLECTION_DETAILS_BY_TYPE,
                    params
                )
                    .then(res => {
                        if (res && res.message && res.message.code === 200) {
                            self.tableData = [
                                ...self.tableData,
                                ...res.resource.results
                            ];
                            self.dynamicPageTitle(self.title);
                            self.dateRange = self.getDateRange(
                                res.resource.start_date,
                                res.resource.end_date
                            );

                            // CONTINUE FETCHING
                            self.nextPage += 1;
                            if (
                                res.resource.results.length ==
                                self.RECORD_PER_PAGE
                            ) {
                                self.timer(self.INTERVAL_TIME).then(() => {
                                    self.init();
                                });
                            } else {
                                self.isLoading = false;
                                self.toggleOptions(true);
                            }
                        } else {
                            throw new Error("Error: Unable to get the report.");
                        }
                    })
                    .catch(e => {
                        console.error(e.message);
                        self.isError = true;
                        self.isDestroyed = true;
                        self.isLoading = false;
                        self.toggleOptions(true);
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    })
                    .finally(() => {
                        self.dynamicPageTitle(self.title);
                    });
            }
        },
        generateClientObject(row) {
            return {
                id: !row.client_info.includes("(deleted)")
                    ? row.encoded_id
                    : null,
                name: row.client_info.replace("(deleted)", ""),
                encoded_id: !row.client_info.includes("(deleted)")
                    ? row.encoded_id
                    : null,
                isactive: !row.client_info.includes("(deleted)")
            };
        },
        toggleOptions(value) {
            // to hide the action menu when data is loading
            this.data.filterDateOption = value;
            this.data.exportOptionCsvOnly = value;
            this.data.refreshOption = value;
        }
    }
};
</script>
